import { HTMLAttributes, ReactNode, isValidElement, cloneElement, ReactElement } from 'react';
import { SectionBlock, ThemeMeta, Appearances } from 'models/SectionBlock';
import cn from 'classnames';
import styles from './section.scss';

type OwnProps = Omit<HTMLAttributes<HTMLDivElement>, 'title'> & {
  title?: ReactNode;
  children?: ReactNode;
  titleClassName?: string;
  contentClassName?: string;
  clear?: boolean;
  isDesktop?: boolean;
  isClear?: boolean;
} & Omit<SectionBlock, 'title'>;

interface OmitProps {
  dispatch: unknown;
}

const surfaceBackground = ({ surface: backgroundColor }: ThemeMeta) => ({
  backgroundColor
});

type SectionTitleProps = Pick<OwnProps, 'title' | 'appearance'> & {
  className?: string;
  color?: string;
  backgroundColor?: string;
};

const isReactElement = (node: ReactNode): node is ReactElement => {
  return isValidElement(node);
};

export const SectionTitle = ({
  title,
  className,
  appearance = Appearances.Primary,
  color,
  backgroundColor
}: SectionTitleProps) => {
  const isTertiary = appearance === Appearances.Tertiary;

  const header = isReactElement(title) ? (
    cloneElement(title, {
      style: { color }
    })
  ) : (
    <h3 style={{ color }}>{title}</h3>
  );

  return (
    <div
      style={{ backgroundColor }}
      className={cn(styles.sectionTitle, className, {
        [styles.largeSectionTitle]: appearance === Appearances.Secondary,
        [styles.tertiary]: isTertiary
      })}
    >
      {header}
    </div>
  );
};

export const Section = (props: OwnProps) => {
  const {
    title,
    children,
    full,
    theme = {},
    className,
    appearance = Appearances.Primary,
    contentClassName,
    dispatch: _,
    component: __,
    isDesktop,
    isClear,
    ...rest
  } = props as OwnProps & OmitProps;

  const isTertiary = appearance === Appearances.Tertiary;

  return (
    <div
      style={surfaceBackground(theme)}
      className={cn(styles.section, className, {
        [styles.tertiary]: isTertiary
      })}
      {...rest}
    >
      {title && (
        <SectionTitle
          color={theme.onSurface}
          appearance={appearance}
          className={cn(styles.title, {
            [styles.desktop]: isDesktop,
            [styles.clear]: isClear
          })}
          title={title}
        />
      )}
      <div
        className={cn(styles.content, contentClassName, {
          [styles.full]: full,
          [styles.desktop]: isDesktop,
          [styles.clear]: isClear
        })}
      >
        {children}
      </div>
    </div>
  );
};
